/**
 * STEP 4: MEASUREMENTS
 * 
 * Main component for the measurements step including:
 * - Capture method selection (Manual/Polycam/Hover)
 * - Area list with add/edit/remove
 * - Area detail view with dimensions and fixtures
 */

import React, { useState } from 'react';
import { Plus, Ruler, AlertTriangle } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getEmptyFixtureState } from '../constants/fixtureDefinitions';
import {
  CaptureMethodSelector,
  PolycamUpload,
  HoverUpload,
  AddAreaModal,
  AreaCard,
  AreaDetail,
} from './measurements';

const Step4Measurements = ({ 
  data, 
  onUpdate, 
  onAddArea,
  onRemoveArea,
  onUpdateArea,
  settings = {}, 
  validationErrors = [],
  disabled = false,
  onSave,
  onPrev,
  onNext,
  saving,
  isComplete,
  fieldVisitId,
}) => {
  // Wrapper for updating data
  const updateData = (newData) => {
    if (onUpdate) {
      onUpdate('measurements', newData);
    }
  };
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  
  // Initialize data structure if needed
  const measurements = data || {
    capture_method: 'manual',
    lidar_file_id: null,
    lidar_processed: false,
    areas: [],
  };

  const handleCaptureMethodChange = (method) => {
    updateData({
      ...measurements,
      capture_method: method,
    });
  };

  // Handle LiDAR import completion
  const handleLidarImportComplete = (importResult) => {
    // Refresh the field visit data after import
    // The parent component (useFieldVisit) will handle this via onSave
    if (onSave) {
      onSave();
    }
  };

  const handleAddArea = (type, label) => {
    const newArea = {
      area_id: uuidv4(),
      type,
      label,
      dimensions: {
        length_ft: null,
        length_in: null,
        width_ft: null,
        width_in: null,
        ceiling_height_ft: 8,
        ceiling_height_in: 0,
        floor_sf: null,
        wall_sf: null,
        perimeter_lf: null,
      },
      fixtures: getEmptyFixtureState(type),
      notes: '',
    };
    
    updateData({
      ...measurements,
      areas: [...(measurements.areas || []), newArea],
    });
    
    // Auto-open the new area for editing
    setSelectedAreaId(newArea.area_id);
  };

  const handleRemoveArea = (areaId) => {
    if (!window.confirm('Are you sure you want to remove this area?')) return;
    
    updateData({
      ...measurements,
      areas: measurements.areas.filter(a => a.area_id !== areaId),
    });
  };

  const handleUpdateDimensions = (areaId, dimensions) => {
    updateData({
      ...measurements,
      areas: measurements.areas.map(a => 
        a.area_id === areaId ? { ...a, dimensions } : a
      ),
    });
  };

  const handleUpdateFixture = (areaId, fixtureKey, value) => {
    updateData({
      ...measurements,
      areas: measurements.areas.map(a => 
        a.area_id === areaId 
          ? { ...a, fixtures: { ...a.fixtures, [fixtureKey]: value } }
          : a
      ),
    });
  };

  const handleUpdateNotes = (areaId, notes) => {
    updateData({
      ...measurements,
      areas: measurements.areas.map(a => 
        a.area_id === areaId ? { ...a, notes } : a
      ),
    });
  };

  // Get selected area
  const selectedArea = selectedAreaId 
    ? measurements.areas?.find(a => a.area_id === selectedAreaId)
    : null;

  // Calculate totals
  const getTotals = () => {
    const areas = measurements.areas || [];
    let totalSF = 0;
    let totalReplace = 0;
    let totalAddNew = 0;
    
    areas.forEach(area => {
      if (area.dimensions?.floor_sf) {
        totalSF += area.dimensions.floor_sf;
      }
      Object.values(area.fixtures || {}).forEach(f => {
        if (f.condition === 'exists_replace') totalReplace++;
        if (f.condition === 'add_new') totalAddNew++;
      });
    });
    
    return { totalSF, totalReplace, totalAddNew };
  };

  const totals = getTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Ruler className="w-6 h-6 text-emerald-400" />
            <h2 className="text-xl font-bold text-slate-100">Measurements</h2>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Capture room dimensions and identify fixtures
          </p>
        </div>
        
        {/* Summary Stats */}
        {measurements.areas?.length > 0 && !selectedAreaId && (
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <div className="text-lg font-bold text-slate-200">{measurements.areas.length}</div>
              <div className="text-xs text-slate-500">Areas</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-slate-200">{totals.totalSF.toFixed(0)}</div>
              <div className="text-xs text-slate-500">Total SF</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-amber-400">{totals.totalReplace}</div>
              <div className="text-xs text-slate-500">Replace</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-400">{totals.totalAddNew}</div>
              <div className="text-xs text-slate-500">Add New</div>
            </div>
          </div>
        )}
      </div>

      {/* Area Detail View */}
      {selectedArea ? (
        <AreaDetail
          area={selectedArea}
          onBack={() => setSelectedAreaId(null)}
          onUpdateDimensions={handleUpdateDimensions}
          onUpdateFixture={handleUpdateFixture}
          onUpdateNotes={handleUpdateNotes}
          disabled={disabled}
        />
      ) : (
        <>
          {/* Capture Method */}
          <CaptureMethodSelector
            value={measurements.capture_method}
            onChange={handleCaptureMethodChange}
            disabled={disabled}
          />
          
          {/* LiDAR Upload Components */}
          {measurements.capture_method === 'polycam' && (
            <PolycamUpload 
              disabled={disabled} 
              fieldVisitId={fieldVisitId}
              onImportComplete={handleLidarImportComplete}
            />
          )}
          
          {measurements.capture_method === 'hover' && (
            <HoverUpload 
              disabled={disabled}
              fieldVisitId={fieldVisitId}
              onImportComplete={handleLidarImportComplete}
            />
          )}
          
          {/* Areas Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                Areas ({measurements.areas?.length || 0})
              </h3>
              <button
                onClick={() => setShowAddModal(true)}
                disabled={disabled}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 
                  text-white text-sm font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Area
              </button>
            </div>
            
            {/* Area List */}
            {measurements.areas?.length > 0 ? (
              <div className="space-y-2">
                {measurements.areas.map(area => (
                  <AreaCard
                    key={area.area_id}
                    area={area}
                    onEdit={setSelectedAreaId}
                    onRemove={handleRemoveArea}
                    disabled={disabled}
                  />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center border-2 border-dashed border-slate-700 rounded-lg">
                <Ruler className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <h4 className="text-slate-400 mb-2">No areas added yet</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Add rooms and areas to capture measurements
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  disabled={disabled}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded"
                >
                  Add First Area
                </button>
              </div>
            )}
          </div>
          
          {/* Validation Warning */}
          {measurements.areas?.length > 0 && totals.totalReplace === 0 && totals.totalAddNew === 0 && (
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-amber-400">No Work Items</h4>
                <p className="text-sm text-slate-400 mt-1">
                  No fixtures have been marked for replacement or addition. Make sure to identify 
                  what work needs to be done in each area.
                </p>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Add Area Modal */}
      <AddAreaModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddArea}
      />
    </div>
  );
};

export default Step4Measurements;
